import React, { useEffect, useCallback, useState } from 'react';
import { gql, useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  YupBoolean,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
  toastWarning,
  YupObject,
  YupStringReq,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import { gqlGetByIdQuery, gqlAdd, gqlUpdate, gqlDelete, FormInput } from '../graphql';
import '../../../../styles/tab-styles.css';
import { FormFields } from './collectionpoint-form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { ButtonCloseModal, RowFieldTooltips } from 'shared-pages/components';
import { Button, Tab, Tabs } from 'react-bootstrap';
import {
  isAfisCyprus,
  isCombatt,
  isFotokiklosi,
  isMedicycle,
  isRebattery,
  isRebatteryLiion,
  requiredString,
} from 'shared-pages/common/constants';
import { CollectionOrdersGridPage } from 'shared-pages/pages/collection-orders';
import { CollectionsGridPage } from 'shared-pages/pages/collections';
import { CollectionpointsGridPage } from '../grid/collectionpoints-grid-page';

import { getRouteByName } from 'app/routes';
import { ContainerOrdersGridPage } from 'shared-pages/pages/container-orders';
import { ContainersStocksTabPage } from './containers-stocks/containers-stocks-table';
import { ContainersQrCodesGridPage } from 'shared-pages';
import { convertFieldToFloat } from 'shared-pages/common/form-helpfunc';
import { useTriggerMailService } from 'shared-pages/hooks/trigger-mail-service';
import { ContactsGridPage } from 'shared-pages';
import { FaFilePdf } from 'react-icons/fa';

// Customize FormShape based on Customer/Company
function useCustomFormShape(base: any) {
  const { t } = useTranslation();
  const reqStr = t(requiredString);

  let formShape = Yup.object(base);

  // Rebattery Liion
  if (isRebatteryLiion) {
    formShape = formShape.concat(
      Yup.object({
        imaCode: YupString.when('categoryId', {
          is: 2,
          then: YupString.required(reqStr),
          otherwise: YupString.nullable().notRequired(),
        }),
        ymeCode: YupString.when('categoryId', {
          is: 2,
          then: YupString.required(reqStr),
          otherwise: YupString.nullable().notRequired(),
        }),
        subregionId: YupNumeric.required(reqStr),
        companyVat: YupString.required(reqStr),
        companyTaxOffice: YupString.required(reqStr),
      }),
    );
  }
  // Fotokiklosi
  else if (isFotokiklosi) {
    formShape = formShape.concat(
      Yup.object({
        addressNomos: YupString.required(reqStr),
      }),
    );
  // Medicycle
  } else if (isMedicycle) {
    formShape = formShape.concat(
      Yup.object({
        phone: YupString.required(reqStr).test(
          'min-digits',
          t('Το τηλέφωνο πρέπει να έχει τουλάχιστον 8 ψηφία'),
          (val) => !!val && val.replace(/\D/g, '').length >= 8,
        ),
        mobile: YupString.test(
          'min-digits-mobile',
          t('Το τηλέφωνο πρέπει να έχει τουλάχιστον 8 ψηφία'),
          (val) => !val || val.replace(/\D/g, '').length >= 8,
        ),
      }),
    );
  }

  return formShape;
}

type Props = {
  isModal?: boolean;
  paramId?: string | undefined;
  modalOnClose?: (id: number) => void | undefined;
  initialValues?: any;
};

/**
 *  Page Component
 */
export const CollectionPointFormPage = ({
  isModal = false,
  paramId = undefined,
  modalOnClose,
  initialValues,
}: Props) => {
  const { handleGraphqlError } = useHandleError('collector');
  const { appStore } = useRootStore();
  const history = useHistory();
  const { id: urlId } = useParams<{ id: string }>();
  const id = isModal ? paramId : urlId;
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();
  const { triggerMailService } = useTriggerMailService();

  const formShape = {
    id: YupNumeric,
    legacyAppId: YupString,
    name: YupString.required(requiredString),
    companyVat: YupString,
    companyTaxOffice: !isAfisCyprus || !isMedicycle ? YupString : YupString,
    imaCode: YupString,
    ymeCode: YupString,
    addressDescription: YupString,
    addressLine1: YupString.required(requiredString),
    addressLine2: YupString,
    addressArea: YupString,
    addressCity: YupString,
    addressNomos: YupString,
    postalCode: YupString,
    phone: YupString.required(requiredString),
    mobile: YupString,
    fax: YupString,
    email:
      isAfisCyprus || isMedicycle
        ? YupString.required(requiredString).test(
            'multi-email',
            t('Το email δεν είναι έγκυρο'),
            (val) => {
              if (!val) return false;
              const emails = val
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);
              return emails.length > 0 && emails.every((e) => Yup.string().email().isValidSync(e));
            },
          )
        : YupString.test('multi-email-optional', t('Το email δεν είναι έγκυρο'), (val) => {
            if (!val) return true;
            const emails = val
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            return emails.every((e) => Yup.string().email().isValidSync(e));
          }),
    website: YupString,
    contactPerson: YupString,
    categoryId: YupNumeric,
    groupId: YupNumeric,
    subgroupId: YupNumeric,
    subsubgroupId: YupNumeric,
    isPubliclyVisibleInMap: YupBoolean,
    recordDate: YupDate,
    isActive: YupBoolean,
    regionId: YupNumeric,
    region: YupObject.nullable(),
    subregionId: YupNumeric,
    subregion: YupObject.nullable(),
    code: YupString,
    notes: YupString,
    addressDistrict: YupString,
    latitude: YupNumeric,
    longitude: YupNumeric,
    customField1: YupString,
    professionId: YupNumeric,
    profession: YupObject.nullable(),
    parentCpId: YupNumeric,
    parentCollectionpoint: YupObject.nullable(),
    cpGroup: YupObject.nullable(),
    cpSubGroup: YupObject.nullable(),
    cpSubsubGroup: YupObject.nullable(),
    gpsUpdateId: YupNumeric,
    gpsDate: YupDate,
    gpsSource: YupString,
  };

  // const validationSchema = Yup.object().shape(formShape);
  const validationSchema = useCustomFormShape(formShape);

  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;
  const location: any = useLocation();
  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery);
  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const [initialNewRecData, setInitialNewRecData] = useState<any>();
  //tab style And state
  const tabStyle = { minHeight: '360px' };
  const [key, setKey] = useState(() => {
    return location.state?.activeTab || 'orders'; // Default to 'orders'
  });
  /** Fetch data or Initialize new record */
  useEffect(() => {
    if (isNew) {
      // initialize new record
      initializeNewRecord();
    } else {
      // fetch data
      get({ variables: { id: idNum } });
    }
  }, [get, id, idNum, initialValues]);

  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    // Grab current location safely
    const current = window.history.state?.usr || location.state || {};

    if (current?.fromQrCodesGrid) {
      history.push(generatePath(getRouteByName('ContainersQrCodes')));
    } else {
      history.push(generatePath(getRouteByName('Collectionpoints')), { ...current });
    }
  }, [history, location.state]);

  /** Αρχικοποίησε Νέα Εγγραφή  */
  const initializeNewRecord = async () => {
    let newRec: any = { isActive: true, ...initialValues };
    setInitialNewRecData(newRec);
  };

  function calcUserCanEdit() {
    let canEdit = appStore?.isPlatformOwner || isNew;
    if (isFotokiklosi) {
      if (appStore.isCollector) canEdit = true;
    }
    const canEditReasoning = '';
    return { canEdit, canEditReasoning };
  }
  const { canEdit, canEditReasoning } = calcUserCanEdit();
  const canDelete = appStore.isPlatformOwner;

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = async (values: FormValuesType, actions) => {
    actions.setSubmitting(false);
    if (!canEdit) return;
    const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
    convertFieldToFloat(mutValues, ['latitude', 'longitude']);

    // Αν είναι νέα εγγραφή τοτε γράφω όλα τα πεδία
    if (isNew) {
      console.log('[IDoc] Add:', mutValues);
      const rec = await addNewRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          const rec = d.data[Object.keys(d.data)[0]];
          toastSuccess(`Επιτυχημένη Προσθήκη Εγγραφής [${rec?.id}]`);
          if (!isModal) {
            handleReturn();
          } else return rec;
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
          return null;
        });
      if (rec) {
        if (isModal) {
          if (modalOnClose) modalOnClose(rec.id);
        }
      }
    } else {
      // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
      console.log('[Applog] Update:', mutValues);
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
          refetch();
          if (isFotokiklosi) triggerMailService();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    if (!canDelete) return;
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  /**
   *   HMA
   */
  const client = useApolloClient();
  async function handleHMAPdfClick(event) {
    const gqlGetImaPdf = gql`
      query getImaPdf($collectionPointId: Int!, $year: Int!) {
        getImaPdf(collectionPointId: $collectionPointId, year: $year)
      }
    `;
    const currentYear = new Date().getFullYear() - 1;
    console.log('HMA');

    try {
      const { data } = await client.query({
        query: gqlGetImaPdf,
        variables: { collectionPointId: parseInt(id), year: currentYear },
        fetchPolicy: 'no-cache',
      });

      const base64 = data?.getImaPdf;
      if (!base64) {
        throw new Error('Δεν υπάρχουν δεδομένα για δημιουργία HMA');
      }

      const blob = new Blob([Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))], {
        type: 'application/pdf',
      });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err: any) {
      const msg =
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.result?.errors?.[0]?.message ||
        err?.message ||
        'Παρουσιάστηκε σφάλμα: Δεν ήταν δυνατή η δημιουργία HMA';
      toastWarning(msg);
    }
    return;
  }

  /**
   * RENDER
   */
  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <>
        <FormCard
          title={
            isNew
              ? `${t('Δήλωση Νέου Σημείου Συλλογής')}`
              : `${t('Σημείο Συλλογής')}: ${data?.name}`
          }
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          onReturn={!isModal ? handleReturn : undefined}
          permitSubmit={false}
          permitDelete={canDelete}
          initialValues={isNew ? initialNewRecData : pickProperties(data)}
          validationSchema={validationSchema}
          enableReinitialize
          idx={idNum}
          injectFooterElements={
            !isModal
              ? () => {
                  if (!isNew && isFotokiklosi) {
                    return (
                      <Button
                        onClick={handleHMAPdfClick}
                        variant="success"
                        style={{ width: '160px' }}
                      >
                        <FaFilePdf className="mb-1" /> ΗΜΑ
                      </Button>
                    );
                  } else return undefined;
                }
              : (formikCtx) => {
                  return (
                    <ButtonCloseModal
                      onClick={() => {
                        modalOnClose(-1);
                      }}
                    />
                  );
                }
          }
        >
          <FormFields
            isNew={isNew}
            updateRecord={updateRecord}
            refetch={refetch}
            data={data}
            canEdit={canEdit}
            isModal={isModal}
            initialValues={initialValues}
          />
          <RowFieldTooltips data={data} />
        </FormCard>

        {/* Tabs */}
        {data && data.id > 0 && (
          <div>
            <Tabs activeKey={key} onSelect={(k) => setKey(k)}>
              {/* Orders Tab */}
              {!isRebattery && (
                <Tab eventKey="orders" title={t('Αιτήσεις Συλλογής')} style={tabStyle}>
                  {key === 'orders' && (
                    <CollectionOrdersGridPage collectionPointId={data.id} isGridInForm />
                  )}
                </Tab>
              )}

              {/* Collections Tab */}
              {!isRebattery && (
                <Tab eventKey="collections" title={t('Συλλογές')} style={tabStyle}>
                  {key === 'collections' && (
                    <CollectionsGridPage collectionPointId={data.id} isGridInForm={true} />
                  )}
                </Tab>
              )}

              {/* Container Orders Tab */}
              {!isRebattery && (
                <Tab
                  eventKey="containerOrders"
                  title={t('Παραγγελίες Συσκευασιών')}
                  style={tabStyle}
                >
                  {key === 'containerOrders' && (
                    <ContainerOrdersGridPage collectionPointId={data.id} isGridInForm={true} />
                  )}
                </Tab>
              )}

              {/* Containers Stock Tab */}
              {!isRebattery && (
                <Tab eventKey="containersStock" title={t('Απόθεμα Συσκευασιών')} style={tabStyle}>
                  {key === 'containersStock' && (
                    <ContainersStocksTabPage collectionPointId={data.id} />
                  )}
                </Tab>
              )}

              {/* QR Codes Tab */}
              {(isAfisCyprus || isMedicycle || isCombatt) && (
                <Tab eventKey="qrCodes" title={t('QR Codes')} style={tabStyle}>
                  {key === 'qrCodes' && (
                    <ContainersQrCodesGridPage collectionPointId={data.id} isGridInForm={true} />
                  )}
                </Tab>
              )}

              {/* Sub Collection Points Tab */}
              {!isRebattery && (
                <Tab eventKey="subCollectionPoints" title={t('Υποκαταστήματα')} style={tabStyle}>
                  {key === 'subCollectionPoints' && (
                    <CollectionpointsGridPage collectionPointId={data.id} isGridInForm={true} />
                  )}
                </Tab>
              )}

              {/* Contacts Tab */}
              {!isRebattery && (
                <Tab eventKey="contacts" title={t('Επαφές')} style={tabStyle}>
                  {key === 'contacts' && (
                    <ContactsGridPage collectionpointId={data.id} isGridInForm={true} />
                  )}
                </Tab>
              )}
            </Tabs>
          </div>
        )}
      </>
    );
  }
};
